import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { FiSettings } from "react-icons/fi";
import { TooltipComponent } from "@syncfusion/ej2-react-popups";
import axios from "axios";
import { Navbar, Footer, Sidebar, ThemeSettings } from "./Components";
import { Dashboard } from "./Pages";
import Login from "./Pages/Login"; // Assuming Login.jsx is in Pages folder
import "./App.css";

import { useStateContext } from "./Contexts/ContextProvider";
import { BaseUrlProvider, useBaseUrl } from "./Contexts/BaseUrlContext";
import UserList from "./Pages/UserList";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import AdminList from "./Pages/AdminList";
import AdminReq from "./Pages/AdminReq";
import ReportedUser from "./Pages/ReportedUser";

import "primereact/resources/themes/lara-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import CreatorList from "./Pages/CreatorList";
import VideoList from "./Pages/VideoList";
import YouTube from "./Pages/YouTube";
import OwnerLink from "./Pages/OwnerLink";
import UserProfile from "./Pages/UserProfile";
import AdsImages from "./Pages/AdsImages";
import AdminPost from "./Pages/AdminPost";
import SubAdmin from "./Pages/SubAdmin";
import GroupList from "./Pages/GroupLIst";
import GroupJoinRequest from "./Pages/GroupJoinRequest";
import WalletList from "./Pages/WalletList";
import WalletAccount from "./Pages/WalletAccount";
import WithdrawalRequest from "./Pages/WithdrawalRequest";
import AcceptedWithdrawal from "./Pages/AcceptedWithdrawal";
import CustomerRequest from "./Pages/CustomerRequest";
import CustomerList from "./Pages/CustomerList";
import CustomerLinkRequest from "./Pages/CustomerLinkRequest";
import GroupActiveLinks from "./Pages/GroupActiveLinks";
import CancelWithdrawal from "./Pages/CancelWithdrawal";
import GroupChat from "./Pages/GroupChat";
import GroupPackages from "./Pages/GroupPackages";
import CustomerPackages from "./Pages/CustomerPackages";
import GroupMembers from "./Pages/GroupMembers";
import AllGroupMembers from "./Pages/AllGroupMembers";
import GroupMessageReport from "./Pages/GroupMessageReport";
import UserMessageReport from "./Pages/UserMessageReport";
import AppUpdates from "./Pages/AppUpdates";
import AdsRewardWalletList from "./Pages/AdsRewardWalletList";
import AdsRewardWalletAccount from "./Pages/AdsRewardWalletAccount";
import Website from "./Pages/Website";
import CreatorSupportAdmin from "./Pages/CreatorSupportAdmin";
import WebsiteAdmin from "./WebsiteAdmin/WebsiteLayout"; // Website (ycnp.in) admin panel

// import Testimonials from './Pages/Testimonial';



/* Ask the server first whether this browser is logged in. Until it answers, show only a loader —
   the panel is never drawn for someone who is not logged in; they go straight to the login page. */
const AuthGate = ({ children }) => {
  const { baseUrl } = useBaseUrl();
  const [state, setState] = React.useState("checking"); // checking | in | out
  useEffect(() => {
    let alive = true;
    const fd = new FormData();
    fd.append("action", "auth-check");
    axios
      .post(`${baseUrl}/auth.php`, fd, { headers: { "Content-Type": "multipart/form-data" }, withCredentials: true })
      .then((res) => alive && setState(res.data && res.data.access !== false ? "in" : "out"))
      .catch(() => alive && setState("out"));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (state === "out") return <Navigate to="/login" replace />;
  if (state === "checking")
    return (
      <div style={{ position: "fixed", inset: 0, background: "#070b1f", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
        <style>{"@keyframes agSpin{to{transform:rotate(360deg)}}"}</style>
        <div style={{ width: 34, height: 34, borderRadius: "50%", border: "3px solid rgba(255,255,255,.15)", borderTopColor: "#a78bfa", animation: "agSpin .8s linear infinite" }} />
      </div>
    );
  return children;
};

// Protected Route component to check authentication
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem("authToken");
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

const App = () => {
  const {
    setCurrentColor,
    setCurrentMode,
    currentMode,
    activeMenu,
    currentColor,
    themeSettings,
    setThemeSettings,
  } = useStateContext();

  useEffect(() => {
    const currentThemeColor = localStorage.getItem("colorMode");
    const currentThemeMode = localStorage.getItem("themeMode");
    if (currentThemeColor && currentThemeMode) {
      setCurrentColor(currentThemeColor);
      setCurrentMode(currentThemeMode);
    }

    if (currentThemeMode === "Dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    console.log("App component mounted");
  }, []);

  return (
    <div className={currentMode === "Dark" ? "dark" : ""}>
      <BrowserRouter>
        <BaseUrlProvider>
          <div className="flex relative dark:bg-main-dark-bg">
            <div className="fixed right-4 bottom-4" style={{ zIndex: "1000" }}>
              <TooltipComponent content="Settings" position="Top">
                <button
                  type="button"
                  onClick={() => setThemeSettings(true)}
                  style={{ background: currentColor, borderRadius: "50%" }}
                  className="text-3xl text-white p-3 hover:drop-shadow-xl hover:bg-light-gray"
                >
                  <FiSettings />
                </button>
              </TooltipComponent>
            </div>
            <Routes>
              {/* Login Route */}
              <Route path="/login" element={<Login />} />
              {/* Website admin panel (switch: App | Website) */}
              <Route path="/website/*" element={<AuthGate><WebsiteAdmin /></AuthGate>} />
              {/* All other routes are protected */}
              <Route
                path="/*"
                element={
                  <AuthGate>
                  <div
                    className="flex relative dark:bg-main-dark-bg"
                    style={{ width: "100%" }}
                  >
                    {activeMenu ? (
                      <div className="w-72 fixed sidebar dark:bg-secondary-dark-bg bg-white">
                        <Sidebar />
                      </div>
                    ) : (
                      <div className="w-0 dark:bg-secondary-dark-bg">
                        <Sidebar />
                      </div>
                    )}
                    <div
                      className={
                        activeMenu
                          ? "dark:bg-main-dark-bg bg-main-bg min-h-screen md:ml-72 w-full"
                          : "bg-main-bg dark:bg-main-dark-bg w-full min-h-screen flex-2"
                      }
                    >
                      <div className="fixed md:static bg-main-bg dark:bg-main-dark-bg navbar w-full">
                        <Navbar />
                      </div>
                      <div className="w-full">
                        {themeSettings && <ThemeSettings />}
                        <Routes>
                          {/* Dashboard */}
                          <Route path="/" element={<Dashboard />} />
                          <Route path="/dashboard" element={<Dashboard />} />
                          <Route path="/app-ads" element={<AdsImages />} />
                          <Route path="/owner-post" element={<AdminPost />} />
                          <Route path="/report" element={<ReportedUser />} />
                          <Route path="/users/all" element={<UserList />} />
                          <Route path="/system/user" element={<SubAdmin />} />
                          <Route
                            path="/users/creator"
                            element={<CreatorList />}
                          />
                          <Route path="/users/admin" element={<AdminList />} />
                          <Route path="/admin-req" element={<AdminReq />} />
                           <Route path="/youtube" element={<YouTube />} />
                           <Route
                             path="/videos/:vid_type"
                             element={<VideoList />}
                           />
                           <Route
                             path="/videos/:vid_type"
                             element={<VideoList />}
                           />
                          <Route
                            path="/owner-link"
                            element={<OwnerLink />}
                          />
                          <Route
                            path="/user-profile/:user_id"
                            element={<UserProfile />}
                          />
                          <Route path="/group/list" element={<GroupList />} />
                          <Route
                            path="/group/join-request"
                            element={<GroupJoinRequest />}
                          />
                           <Route
                             path="/wallet-account/:user_id"
                             element={<WalletAccount />}
                           />
                           <Route
                             path="/ads-reward-wallet-account/:user_id"
                             element={<AdsRewardWalletAccount />}
                           />
                           <Route
                             path="/group/wallet"
                             element={<WalletList />}
                           />
                           <Route
                             path="/ads-reward-wallet"
                             element={<AdsRewardWalletList />}
                           />
                          <Route
                            path="/wallet/withdrawal-req"
                            element={<WithdrawalRequest />}
                          />
                          <Route
                            path="/wallet/withdrawal"
                            element={<AcceptedWithdrawal />}
                          />
                          <Route
                            path="/wallet/withdrawal-cancel"
                            element={<CancelWithdrawal />}
                          />
                          <Route
                            path="/customer/list"
                            element={<CustomerList />}
                          />
                          <Route
                            path="/customer/request"
                            element={<CustomerRequest />}
                          />
                          <Route
                            path="/customer/link/:platform"
                            element={<CustomerLinkRequest />}
                          />
                          <Route
                            path="/customer/link/active/:platform"
                            element={<GroupActiveLinks />}
                          />
                          <Route path="/group-chat/:groupId" element={<GroupChat />} />
                          <Route path="/group/members/:groupId" element={<GroupMembers />} />
                          <Route path="/group/all-members" element={<AllGroupMembers />} />
                          <Route path="/group/message-reports" element={<GroupMessageReport />} />
                          <Route path="/user/message-reports" element={<UserMessageReport />} />
                          <Route path="/group-packages" element={<GroupPackages />} />
                          <Route path="/customer-packages" element={<CustomerPackages />} />
                          <Route path="/app-updates" element={<AppUpdates />} />
                          <Route path="/creator-support" element={<CreatorSupportAdmin />} />
                          <Route path="/website" element={<Website />} />
                        </Routes>
                      </div>
                      <Footer />
                    </div>
                  </div>
                  </AuthGate>
                }
              />
            </Routes>
          </div>
        </BaseUrlProvider>
      </BrowserRouter>
    </div>
  );
};

export default App;
